
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {SymbolGroupDeleteResponse} from './SymbolGroupDeleteResponse';

/**
 * SymbolGroupDeleteRequest struct
 */
class SymbolGroupDeleteRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} symbolGroupId
   * @param {string=} symbolGroupName
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), symbolGroupId = null, symbolGroupName = null) {
    super()
    super.copy(parent)
    this.SymbolGroupId = symbolGroupId
    this.SymbolGroupName = symbolGroupName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SymbolGroupDeleteRequest}
   * @param {!SymbolGroupDeleteRequest} other Other struct
   * @returns {!SymbolGroupDeleteRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.SymbolGroupId != null) {
      this.SymbolGroupId = new UUID(other.SymbolGroupId)
    } else {
      this.SymbolGroupId = null
    }
    if (other.SymbolGroupName != null) {
      this.SymbolGroupName = other.SymbolGroupName
    } else {
      this.SymbolGroupName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SymbolGroupDeleteRequest}
   * @returns {!SymbolGroupDeleteRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SymbolGroupDeleteRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SymbolGroupDeleteRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SymbolGroupDeleteRequest}
   * @param {!SymbolGroupDeleteRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SymbolGroupDeleteRequest)) {
      throw new TypeError('Instance of SymbolGroupDeleteRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SymbolGroupDeleteRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      SymbolGroupId: ((this.SymbolGroupId != null) ? this.SymbolGroupId.toString() : null),
      SymbolGroupName: ((this.SymbolGroupName != null) ? this.SymbolGroupName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SymbolGroupDeleteRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SymbolGroupDeleteRequest} other Object value
   * @returns {!SymbolGroupDeleteRequest} Created struct
   */
  static fromObject (other) {
    return new SymbolGroupDeleteRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SymbolGroupDeleteRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SymbolGroupDeleteRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SymbolGroupDeleteRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6104
  }
}

export { SymbolGroupDeleteRequest };

/**
 * Fast Binary Encoding SymbolGroupDeleteRequest field model
 */
class FieldModelSymbolGroupDeleteRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.SymbolGroupId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.SymbolGroupName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.SymbolGroupId.fbeOffset + this.SymbolGroupId.fbeSize), buffer, this.SymbolGroupId.fbeOffset + this.SymbolGroupId.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.SymbolGroupId.fbeSize + this.SymbolGroupName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.SymbolGroupId.fbeExtra + this.SymbolGroupName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSymbolGroupDeleteRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6104
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SymbolGroupId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolGroupId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolGroupId.fbeSize

    if ((fbeCurrentSize + this.SymbolGroupName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolGroupName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolGroupName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @param {!SymbolGroupDeleteRequest} fbeValue Default value, defaults is new SymbolGroupDeleteRequest()
   * @returns {!SymbolGroupDeleteRequest} SymbolGroupDeleteRequest value
   */
  get (fbeValue = new SymbolGroupDeleteRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @param {!SymbolGroupDeleteRequest} fbeValue SymbolGroupDeleteRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SymbolGroupId.fbeSize) <= fbeStructSize) {
      fbeValue.SymbolGroupId = this.SymbolGroupId.get()
    } else {
      fbeValue.SymbolGroupId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolGroupId.fbeSize

    if ((fbeCurrentSize + this.SymbolGroupName.fbeSize) <= fbeStructSize) {
      fbeValue.SymbolGroupName = this.SymbolGroupName.get()
    } else {
      fbeValue.SymbolGroupName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolGroupName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @param {!SymbolGroupDeleteRequest} fbeValue SymbolGroupDeleteRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSymbolGroupDeleteRequest}
   * @param {SymbolGroupDeleteRequest} fbeValue SymbolGroupDeleteRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.SymbolGroupId.set(fbeValue.SymbolGroupId)
    this.SymbolGroupName.set(fbeValue.SymbolGroupName)
  }
}

export { FieldModelSymbolGroupDeleteRequest };

/**
 * Fast Binary Encoding SymbolGroupDeleteRequest model
 */
class SymbolGroupDeleteRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSymbolGroupDeleteRequest(this.buffer, 4)
  }

  /**
   * Get the SymbolGroupDeleteRequest model
   * @this {!SymbolGroupDeleteRequestModel}
   * @returns {!FieldModelSymbolGroupDeleteRequest} model SymbolGroupDeleteRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SymbolGroupDeleteRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SymbolGroupDeleteRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SymbolGroupDeleteRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SymbolGroupDeleteRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSymbolGroupDeleteRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SymbolGroupDeleteRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SymbolGroupDeleteRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SymbolGroupDeleteRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SymbolGroupDeleteRequestModel}
   * @param {!SymbolGroupDeleteRequest} value SymbolGroupDeleteRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SymbolGroupDeleteRequestModel}
   * @param {!SymbolGroupDeleteRequest} value SymbolGroupDeleteRequest value, defaults is new SymbolGroupDeleteRequest()
   * @return {!object} Deserialized SymbolGroupDeleteRequest value and its size
   */
  deserialize (value = new SymbolGroupDeleteRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SymbolGroupDeleteRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SymbolGroupDeleteRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SymbolGroupDeleteRequestModel}
   * @param {!number} prev Previous SymbolGroupDeleteRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SymbolGroupDeleteRequestModel };
SymbolGroupDeleteRequest.__has_response = true;
SymbolGroupDeleteRequest.__response_class = SymbolGroupDeleteResponse;
